/* eslint-disable consistent-return */
import * as dayjs from "dayjs";
import { DateTime } from "luxon";

import { REQUIRED_TICKETS_CONDITION } from "../constants";

const compareDatesAreSameDay = (timestamp1, timestamp2) => {
  if (!timestamp1 || !timestamp2) {
    return false;
  }

  const timestamp1Milis = DateTime.fromMillis(timestamp1, {
    zone: "Europe/Copenhagen",
  });
  const timestamp2Milis = DateTime.fromMillis(timestamp2, {
    zone: "Europe/Copenhagen",
  });

  const isTheSameDate = timestamp1Milis.hasSame(timestamp2Milis, "day");

  return isTheSameDate;
};

const requredAdmissionTicketsLeft = (
  admisionTicketId,
  admisionTickets,
  summary
) => {
  let res = [];

  const selectedTicket = admisionTickets.find(
    (ticket) => ticket.id === admisionTicketId
  );

  const isTicketIdInSummary = (id) =>
    summary.selectedAdmissionTickets.some((ticket) => ticket.id === id);

  if (selectedTicket.required_tickets.length > 0) {
    const unselectedRequiredTickets = selectedTicket.required_tickets.filter(
      (reqTicketId) => !isTicketIdInSummary(reqTicketId)
    );

    if (
      selectedTicket.required_tickets_condition ===
      REQUIRED_TICKETS_CONDITION.ALL
    ) {
      res = unselectedRequiredTickets;
    } else if (
      selectedTicket.required_tickets_condition ===
      REQUIRED_TICKETS_CONDITION.ANY
    ) {
      const isAnySelected =
        selectedTicket.required_tickets.some(isTicketIdInSummary);
      res = isAnySelected ? [] : selectedTicket.required_tickets;
    }
  }

  return { id: admisionTicketId, requredTicketsLeft: res };
};

const getAdmissionTicketNameById = (id, admissionTicketsArray) => {
  const admissionTicket = admissionTicketsArray.find(
    (ticket) => ticket.id === id
  );
  return admissionTicket?.name;
};

const getAdmissionTicketNames = (
  requiredTickets,
  admissionTicketsArray,
  requiredTicketsCondition
) => {
  const ticketNames = requiredTickets.map((ticketId) => {
    const admissionTicket = admissionTicketsArray.find(
      (ticket) => ticket.id === ticketId
    );
    return admissionTicket?.name;
  });
  if (requiredTicketsCondition === "all") {
    return ticketNames.filter(Boolean).join(", ");
  } else if (requiredTicketsCondition === "any") {
    return ticketNames.filter(Boolean).join("/");
  }
  return "";
};

const getIndexOfSelectedDateInDates = (selectedDateTimestamp, dates) => {
  const arr = dates.find((date) =>
    compareDatesAreSameDay(date.date, selectedDateTimestamp)
  );

  return dates.indexOf(arr);
};

const getTimestampMillisBasedOnEntryNo = (
  dates,
  selectedDate,
  selectedTimeslot
) =>
  dates
    ?.find((date) => date.date === selectedDate)
    ?.prices?.[0]?.timeslots.find(
      (timeslot) => timeslot.entry_no === selectedTimeslot
    )?.timestamp_in_milis;

const getPriceOfAdmissionTicketById = (
  id,
  admissionTicketsArray,
  selectedDate = null,
  dates = []
) => {
  const admissionTicket = admissionTicketsArray.find(
    (ticket) => ticket.id === id
  );
  if (dates.length === 0) {
    return admissionTicket.price;
  }
  if (selectedDate && dates.length > 0) {
    const selectedDay = dates.find((d) =>
      compareDatesAreSameDay(d.date, selectedDate)
    );
    const priceOnSelectedDay = selectedDay?.prices.find(
      (price) => price.product_sku === admissionTicket.product_sku
    );
    if (priceOnSelectedDay) {
      return { min: priceOnSelectedDay.price, max: priceOnSelectedDay.price };
    }
  }
  return admissionTicket.price;
};

const calculateIndicativeTotalPriceOfAdmissionTickets = (
  selectedAdmissionTickets,
  allAdmissionTickets
) => {
  const prices = selectedAdmissionTickets.map((ticket) => {
    const { min, max } = getPriceOfAdmissionTicketById(
      ticket.id,
      allAdmissionTickets
    );
    return { totalMin: min * ticket.qty, totalMax: max * ticket.qty };
  });
  const totalMinSum = prices.reduce((n, { totalMin }) => n + totalMin, 0);
  const totalMaxSum = prices.reduce((n, { totalMax }) => n + totalMax, 0);

  return { totalMin: totalMinSum, totalMax: totalMaxSum };
};

const calculateTotalPricesOnSelectedDate = (
  summary,
  dates,
  admissionTickets
) => {
  const { selectedDate, selectedAdmissionTickets, selectedAddonTickets } =
    summary;
  let discount = 0;
  let admissionsTotalPrice = 0;
  let addonsTotalPrices = [];
  let total = 0;

  if (selectedDate && dates.length > 0) {
    if (selectedAdmissionTickets.length > 0) {
      const date = dates.find((date) =>
        compareDatesAreSameDay(date.date, selectedDate)
      );
      discount = date?.prices
        .flatMap((price) => (price.discount < 0 ? price.discount : 0))
        .reduce((prev, next) => prev + next);
      admissionsTotalPrice = totalPriceOfSelectedAdmissionsOnDay(
        date,
        selectedAdmissionTickets
      );
      total += admissionsTotalPrice;
    }
    if (Object.keys(selectedAddonTickets).length > 0) {
      addonsTotalPrices = Object.keys(selectedAddonTickets).map((key) => {
        const { price, qty } = selectedAddonTickets[key];
        total += qty * price;
        return {
          addonId: key,
          totalQty: qty,
          pricePerPerson: price,
          totalPrice: price * qty,
        };
      });
    }
  } else {
    const { totalMin, totalMax } =
      calculateIndicativeTotalPriceOfAdmissionTickets(
        selectedAdmissionTickets,
        admissionTickets
      );
    admissionsTotalPrice = `${totalMin} - ${totalMax}`;
    const isPriceRange = totalMin !== totalMax;
    total = isPriceRange ? `${totalMin} - ${totalMax}` : totalMin;
  }

  return {
    admissionsTotalPrice,
    addonsTotalPrices,
    totalWithDiscount: total,
    discount,
  };
};

const calculateTotalPricesForActivitiesOnly = (state) => {
  const { selectedAddonTickets } = state.summary;
  let addonsTotalPrices = [];
  let total = 0;

  if (Object.keys(selectedAddonTickets).length > 0) {
    addonsTotalPrices = Object.keys(selectedAddonTickets).map((key) => {
      const { price, qty } = selectedAddonTickets[key];
      total += qty * price;
      return {
        addonId: key,
        totalQty: qty,
        pricePerPerson: price,
        totalPrice: price * qty,
      };
    });
  }

  return {
    addonsTotalPrices,
    totalWithDiscount: total,
  };
};

const isActiveStepValid = (stepper) => {
  const { active } = stepper;
  return stepper.steps[active].isValid;
};

const selectedAdmissionTicketsQuantityGreaterThanMax = (
  selectedAdmissionTickets,
  max = 30
) => {
  const maxQtyExceeded = selectedAdmissionTickets.filter(
    (ticket) => ticket.qty > max
  );
  return maxQtyExceeded.map((ticket) => ticket.id);
};

const totalQtyOfSelectedAdmissions = (selectedAdmisions) => {
  let sum = 0;
  selectedAdmisions.forEach((ticket) => {
    sum += ticket.qty;
  });
  return sum;
};

const totalPriceOfSelectedAdmissionsOnDay = (
  currDay,
  selectedAdmissionTickets
) => {
  const pricesOnDay = selectedAdmissionTickets.map((ticket) => {
    const ticketPrice = currDay?.prices.find(
      (price) => price.product_sku === ticket.productSku
    );
    if (ticketPrice) {
      return ticketPrice.price * ticket.qty;
    }
  });
  const totalPriceOnDay = pricesOnDay.reduce(
    (partialSum, a) => partialSum + a,
    0
  );
  return totalPriceOnDay;
};

const totalDiscountOfSelectedAdmissionsOnDay = (
  currDay,
  selectedAdmissionTickets
) => {
  const discountsOnDay = selectedAdmissionTickets.map((ticket) => {
    const priceInfo = currDay?.prices.find(
      (price) => price.product_sku === ticket.productSku
    );
    if (priceInfo && priceInfo.discount < 0) {
      return Math.abs(priceInfo.discount);
    }
    return 0;
  });
  const totalDiscountOnDay = discountsOnDay.reduce(
    (partialSum, a) => partialSum + a,
    0
  );
  return totalDiscountOnDay;
};

const getTicketsLeft = (currentDay) =>
  currentDay?.prices
    .flatMap((price) =>
      price?.timeslots
        .slice(1)
        .flatMap((timeslot) => Number(timeslot.tickets_left))
        ?.reduce((prev, next) => prev + next, 0)
    )
    ?.reduce((prev, next) => prev + next, 0);

const getAddonEventsOnDate = (dateTimestamp, addons) => {
  const filtered = addons.reduce((acc, curr) => {
    const filteredDates = curr.dates.filter((date) =>
      compareDatesAreSameDay(date.date, dateTimestamp)
    );
    if (filteredDates.length === 0) {
      return acc;
    }
    acc.push({ ...curr, dates: filteredDates });
    return acc;
  }, []);
  return filtered;
};

const getDateItemData = (currDate, dates, selectedAdmissionTickets, addons) => {
  const currentDay = dates.find((d) => d.date === currDate);
  const isDayActive = currentDay?.prices?.[0]?.active;
  const totalQty = totalQtyOfSelectedAdmissions(selectedAdmissionTickets);
  const ticketsLeft = getTicketsLeft(currentDay);
  const dateItemData = {
    date: currentDay.date,
    discount: Math.abs(
      totalDiscountOfSelectedAdmissionsOnDay(
        currentDay,
        selectedAdmissionTickets
      )
    ),
    totalTicketsLeft: ticketsLeft,
    isSoldOut: ticketsLeft < totalQty,
    isClosed: isDayActive === "false",
    totalPrice: totalPriceOfSelectedAdmissionsOnDay(
      currentDay,
      selectedAdmissionTickets
    ),
    addonEvents: getAddonEventsOnDate(currDate, addons),
  };
  return dateItemData;
};

const getDateTimeSlots = (currDate, dates) => {
  const currentDay = dates.find((d) => d.date === currDate);
  const timeslots = [];
  currentDay?.prices?.forEach((price) => {
    price?.timeslots?.forEach((timeslot) => {
      const now = dayjs().add(60, "minute").valueOf();
      if (
        !timeslots.includes(timeslot.timestamp_in_milis) &&
        timeslot.timestamp_in_milis > now
      ) {
        timeslots.push(timeslot);
      }
    });
  });
  function filterTimeslots(timeslots) {
    const uniqueTimestamps = new Set();
    const filteredTimeslots = [];
    for (const slot of timeslots) {
      if (slot.admission_code !== "VISITCARLSBERG") {
        continue;
      }

      if (!slot.timestamp_in_milis) {
        const key = `${slot.start_date}-${slot.start_time}`;
        if (!uniqueTimestamps.has(key)) {
          uniqueTimestamps.add(key);
          filteredTimeslots.push(slot);
        }
      } else if (!uniqueTimestamps.has(slot.timestamp_in_milis)) {
        uniqueTimestamps.add(slot.timestamp_in_milis);
        filteredTimeslots.push(slot);
      }
    }

    return filteredTimeslots;
  }
  return filterTimeslots(timeslots);
};

const calculateAllowedActivityAmount = (addonId, state) => {
  const addon = state?.addons.find((addon) => addon.id === addonId);
  let allowedAmount = 0;

  if (addon) {
    const admissionCode = addon.dates?.[0]?.timeslots?.[0]?.admission_code;

    state.summary.selectedAdmissionTickets.forEach(
      (selectedAdmissionTicket) => {
        const allowAdmissionCode = selectedAdmissionTicket.admission.find(
          (admission) => admission.code === admissionCode
        );

        if (allowAdmissionCode) {
          allowedAmount += selectedAdmissionTicket.qty;
        }
      }
    );
  }

  return allowedAmount;
};

const getTicketsLeftByTimeslotInMilis = (timeslots, timeslotVal) =>
  timeslots.find((timeslt) => timeslt.timestamp_in_milis === timeslotVal)
    .tickets_left;

const getAddonEventTitleById = (addons, addonId) => {
  const title = addons.find((addon) => addon.id === addonId)?.title;
  return title;
};

const isSummaryEmpty = (summary) => {
  const {
    selectedAdmissionTickets,
    selectedAddonTickets,
    selectedGiftCardVariants,
  } = summary;
  const isAddonsEmpty = Object.keys(selectedAddonTickets).length === 0;
  const isAdmissionsEmpty = selectedAdmissionTickets.length === 0;
  const isGiftCardEmpty =
    !selectedGiftCardVariants ||
    Object.keys(selectedGiftCardVariants).length === 0;
  return isAddonsEmpty && isAdmissionsEmpty && isGiftCardEmpty;
};

const calculateTotalPricesForGiftCard = (summary) => {
  const variants = summary.selectedGiftCardVariants || {};
  const total = Object.values(variants).reduce(
    (sum, variant) =>
      sum + Number(variant.price?.amount || 0) * (variant.qty || 1),
    0
  );
  return {
    addonsTotalPrices: [],
    totalWithDiscount: total,
    discount: 0,
  };
};

// --------------------STEP VALIDATION FUNCTIONS -----------------------------------
const stepOneValidityCheck = (summary, admissionTickets) => {
  const { selectedAdmissionTickets } = summary;
  let warnings = {};
  let isAllRequiredTickets = false;
  let isSelectedQuantityValid = false;
  let isSumQuantityUnderThirty = false;

  if (selectedAdmissionTickets.length > 0) {
    // CHECK 1: Are all tickets is summary have fulfiled all required tickets to buy
    const checkRequredTickets = selectedAdmissionTickets.map((ticket) => {
      const requredLeftToBuy = requredAdmissionTicketsLeft(
        ticket.id,
        admissionTickets,
        summary
      );
      return requredLeftToBuy;
    });
    const isRequredTicketsLeft = checkRequredTickets.some(
      (ticket) => ticket.requredTicketsLeft.length > 0
    );
    isAllRequiredTickets = !isRequredTicketsLeft;

    checkRequredTickets.forEach((ticket) => {
      if (ticket.requredTicketsLeft.length > 0) {
        isAllRequiredTickets = false;
        warnings = {
          ...warnings,
          requiredAdmissionTickets: {
            ...warnings.requiredAdmissionTickets,
            [ticket.id]: ticket.requredTicketsLeft,
          },
        };
      }
    });
    // CHECK 2: Is quantity of admission ticket is summary lower than maximum: 30
    const ticketsExeededQtyLimit =
      selectedAdmissionTicketsQuantityGreaterThanMax(selectedAdmissionTickets);
    if (ticketsExeededQtyLimit.length > 0) {
      warnings = {
        ...warnings,
        qtyLimitExceeded: ticketsExeededQtyLimit,
      };
      isSelectedQuantityValid = false;
    } else {
      isSelectedQuantityValid = true;
    }
  }
  // CHECK 3: Is sum quantity lower than 30
  const totalTickets = selectedAdmissionTickets.reduce(
    (sum, ticket) => sum + ticket.qty,
    0
  );
  if (totalTickets > 30) {
    isSumQuantityUnderThirty = false;
  } else {
    isSumQuantityUnderThirty = true;
  }
  const isStepOneValid =
    isAllRequiredTickets && isSelectedQuantityValid && isSumQuantityUnderThirty;
  return { isStepOneValid, warnings };
};

const stepTwoValidityCheck = (state) => {
  let warnings = {};
  if (!state.summary.selectedDate) {
    warnings = {
      ...warnings,
      noDateSelected: "Select date first!",
    };
    return { isStepTwoValid: false, warnings };
  }

  if (!state.summary.selectedTimeslot) {
    warnings = {
      ...warnings,
      noTimeslotSelected: "Select timeslot first!",
    };
    return { isStepTwoValid: false, warnings };
  }

  return { isStepTwoValid: true, warnings };
};

const stepThreeValidityCheck = (summary) => {
  let warnings = {};

  const { selectedAdmissionTickets, selectedAddonTickets } = summary;
  if (
    selectedAdmissionTickets.length === 0 &&
    Object.keys(selectedAddonTickets).length === 0
  ) {
    warnings = {
      ...warnings,
      noTicketsSelected: "Admission and/or Addon tickets must be selected!",
    };
    return { isStepThreeValid: false, warnings };
  }
  return { isStepThreeValid: true, warnings };
};

const checkIsTimeslotTaken = (
  currentTimeslot,
  selectedTimeslots,
  addonId,
  isActivitiesOnly
) => {
  if (!currentTimeslot) {
    return;
  }

  for (const selectedSlot of selectedTimeslots) {
    if (addonId === selectedSlot.addonId) {
      return false;
    }

    if (
      isActivitiesOnly &&
      currentTimeslot.start_date !== selectedSlot.start_date
    ) {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (currentTimeslot.start_date !== selectedSlot.start_date) {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (
      (currentTimeslot.start_time >= selectedSlot.start_time &&
        currentTimeslot.start_time < selectedSlot.end_time) ||
      (currentTimeslot.end_time > selectedSlot.start_time &&
        currentTimeslot.end_time <= selectedSlot.end_time) ||
      (currentTimeslot.start_time <= selectedSlot.start_time &&
        currentTimeslot.end_time >= selectedSlot.end_time)
    ) {
      return true;
    }
  }
  return false;
};

const getCCCardDateRange = (cards) => {
  if (!cards.length) {
    return null;
  }

  const dateFormat = "dd-MM-yyyy HH:mm";

  let maxStartDate = DateTime.fromFormat(cards[0].date_range[0], dateFormat);
  let minEndDate = DateTime.fromFormat(cards[0].date_range[1], dateFormat);

  cards.forEach((card) => {
    const startDate = DateTime.fromFormat(card.date_range[0], dateFormat);
    const endDate = DateTime.fromFormat(card.date_range[1], dateFormat);

    if (startDate > maxStartDate) {
      maxStartDate = startDate;
    }

    if (endDate < minEndDate) {
      minEndDate = endDate;
    }
  });

  return [maxStartDate.toMillis(), minEndDate.toMillis()];
};

const getDiscountCodeFromUrl = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("discount");
  } catch (error) {
    console.error("Failed to parse URL parameters:", error);
    return null;
  }
};

export const HELPERS = {
  calculateTotalPricesForActivitiesOnly,
  calculateTotalPricesForGiftCard,
  calculateTotalPricesOnSelectedDate,
  calculateAllowedActivityAmount,
  compareDatesAreSameDay,
  getAddonEventTitleById,
  getAddonEventsOnDate,
  getAdmissionTicketNameById,
  getAdmissionTicketNames,
  getDateItemData,
  getIndexOfSelectedDateInDates,
  getPriceOfAdmissionTicketById,
  getTicketsLeftByTimeslotInMilis,
  getTimestampMillisBasedOnEntryNo,
  isActiveStepValid,
  isSummaryEmpty,
  requredAdmissionTicketsLeft,
  stepOneValidityCheck,
  stepThreeValidityCheck,
  stepTwoValidityCheck,
  getDateTimeSlots,
  checkIsTimeslotTaken,
  getCCCardDateRange,
  getDiscountCodeFromUrl,
};
